import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { OrdersService, TrackingInfo } from '@evermile/customer-tracking';
import { addDays, format, isSameDay } from 'date-fns';
import { DateProposals } from '@evermile/customer-tracking/model/dateProposals';
import { DisplayDateFormatterPipe } from '../../../pipes/display-date-formatter.pipe';
import { UKTimePipe } from '../../../pipes/uk-date-formatter.pipe';
import { TimeslotProposal } from '@evermile/customer-tracking/model/timeslotProposal';
import { EditOrderService } from '../../../services/edit-order.service';
import { getMerchantSlotWindow } from 'common-logic';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { CalendarHeaderComponent } from '../../../calendar-header/calendar-header.component';
import { Subscription } from 'rxjs';
import { GtmTracking } from '../../../services/gtm.service';
import { ProposalLabel } from '@evermile/api';

interface SummarizedProposal {
  dayPart: string;
  available: boolean;
  proposalId?: string;
  current: boolean;
  time?: string;
  sortMs?: number;
  durationMs?: number;
}
interface UIproposal extends DateProposals {
  formattedTime?: string;
  summarizedProposals?: SummarizedProposal[];
  requestedDate?: boolean;
  isEmpty?: boolean;
  isDayOfTheOriginalOrder?: boolean;
}

interface UIproposalDayBlock {
  empty?: boolean;
  formattedTime?: string;
  requestedDate?: boolean;
  emptyDates?: string[];
  dateBlock?: UIproposal;
  firstInShort?: boolean;
}
class CustomDateAdapter extends NativeDateAdapter {
  override getDayOfWeekNames(): string[] {
    const SHORT_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return SHORT_NAMES;
  }
}

@Component({
  selector: 'common-edit-order-change-date-time',
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
    },
  ],
  templateUrl: './edit-order-change-date-time.component.html',
  styleUrls: ['./edit-order-change-date-time.component.scss'],
})
export class EditOrderChangeDateTimeComponent implements OnInit, OnDestroy {
  @Input() order!: TrackingInfo;
  @Input() subTitle?: string;
  @Input() brandColor?: string;
  @Input() textColor?: string;
  @Input() index?: number;
  @Input() note?: string;
  @Input() displayCancelButton = false;
  @Input() externalProposals?: Array<DateProposals>;
  @Output() emitCancel = new EventEmitter();

  showEarlierOptionsButton = false;
  showLaterOptionsButton = false;

  proposalsDayBlocks: Array<UIproposalDayBlock> = [];
  proposalsDayBlocksShorter?: Array<UIproposalDayBlock>;
  displayProposalsDayBlocks?: Array<UIproposalDayBlock>;
  isCalendarOpen = false;
  wasCurrentSelected = false;

  loading = false;
  selectedTime?: string;

  today = new Date();
  maxDate = addDays(new Date(), 30);
  isPageActive = false;
  indexChangeSub?: Subscription;
  constructor(
    private apiEditOrderService: OrdersService,
    private editOrderService: EditOrderService,
    private gtm: GtmTracking,
    private displayDateFormatterPipe: DisplayDateFormatterPipe,
    private timePipeUK: UKTimePipe,
  ) {}

  ngOnInit() {
    if (this.externalProposals) {
      this.handleProposals(this.externalProposals);
    } else {
      this.sendPostQuote();
    }
    this.indexChangeSub = this.editOrderService.afterIndexChange$.subscribe((data) => {
      if (data === this.index) {
        setTimeout(() => {
          this.isPageActive = true;
        }, 500);
      } else {
        this.isPageActive = false;
      }
    });
  }

  ngOnDestroy() {
    this.indexChangeSub?.unsubscribe();
  }

  sendPostQuote(dateParam?: string) {
    const date = dateParam || this.order?.trackingInfo?.dropoffWindowStart || '';
    this.apiEditOrderService
      .orderEditQuotePost({
        address: this.order.destinationAddress,
        date: format(new Date(date), 'y-MM-dd'),
      })
      .subscribe((res) => {
        this.handleProposals(res.dateProposals, dateParam);
      });
  }

  handleProposals(proposals: Array<DateProposals>, dateParam?: string) {
    this.showLaterOptionsButton = false;
    this.showEarlierOptionsButton = false;
    if (!this.editOrderService.areThereValidProposals(proposals)) {
      this.gtm.invalidForm('Edit order post quote', proposals, {});
      this.editOrderService.showErrorScreen.emit();
    }
    const tempProposals = [...proposals];
    tempProposals.forEach((p: any) => {
      p.formattedTime = this.displayDateFormatterPipe.transformMerchantTime(p.date, 'E, MMM d') || '';
      p.requestedDate = this.isRequestedDate(p.date, dateParam);
      p.proposals.forEach((timeProposal: TimeslotProposal) => {
        if (
          timeProposal.proposal &&
          [ProposalLabel.Morning, ProposalLabel.Afternoon, ProposalLabel.Evening].includes(timeProposal.label)
        ) {
          const slot = getMerchantSlotWindow(
            timeProposal.proposal?.estimatedPickup,
            timeProposal.proposal?.estimatedDropoff,
          );
          const editedUIProp = {
            dayPart: timeProposal.label,
            available: !!timeProposal.proposal,
            proposalId: timeProposal.proposal?.id,
            current: this.isCurrentProposal(timeProposal, p.date),
            time: slot
              ? `${this.timePipeUK.transformWithFormat(slot.start, 'h:mmaaa')} - ${this.timePipeUK.transformWithFormat(slot.end, 'h:mmaaa')}`
              : undefined,
            sortMs: slot ? +new Date(slot.start) : undefined,
            durationMs: slot ? +new Date(slot.end) - +new Date(slot.start) : undefined,
          };
          p.summarizedProposals = [...(p.summarizedProposals || []), editedUIProp];
        }
      });
      p.summarizedProposals?.sort((a: SummarizedProposal, b: SummarizedProposal) => {
        const aDur = a.durationMs ?? Number.POSITIVE_INFINITY;
        const bDur = b.durationMs ?? Number.POSITIVE_INFINITY;
        if (aDur !== bDur) return aDur - bDur;
        return (a.sortMs ?? Number.POSITIVE_INFINITY) - (b.sortMs ?? Number.POSITIVE_INFINITY);
      });
      p.isEmpty = p.summarizedProposals.every((p: SummarizedProposal) => !p.available);
      p.isDayOfTheOriginalOrder =
        p.date === format(new Date(this.order.trackingInfo.dropoffWindowStart || ''), 'yyyy-MM-dd');
      this.forceCurrentOptionWhenNotProvided(p, this.order);
    });
    this.setProposalsDateBlocks(tempProposals);
    if (dateParam) {
      this.displayProposalsDayBlocks = this.proposalsDayBlocks;
      setTimeout(() => {
        const currentElement = document.querySelector('.date-header.requested');
        currentElement?.scrollIntoView({ behavior: 'instant', block: 'center' });
      }, 0);
    } else {
      this.proposalsDayBlocksShorter = this.getShorterPropArray([...this.proposalsDayBlocks!]);
      this.proposalsDayBlocksShorter[0].firstInShort = true;
      this.displayProposalsDayBlocks = this.proposalsDayBlocksShorter;
      setTimeout(() => {
        const currentElement = document.querySelector('button.current');
        //currentElement?.scrollIntoView({ behavior: 'instant', block:'center' });
      }, 0);
      if (!this.displayProposalsDayBlocks) return;
      this.showLaterOptionsButton = !!this.proposalsDayBlocks.find(
        (p) =>
          !p.empty &&
          new Date(p.dateBlock!.date).getTime() >
            new Date(
              this.displayProposalsDayBlocks![this.displayProposalsDayBlocks!.length - 1]?.dateBlock?.date || '',
            ).getTime(),
      );
      this.showEarlierOptionsButton = !!this.proposalsDayBlocks.find(
        (p) =>
          !p.empty &&
          new Date(p.dateBlock!.date).getTime() <
            new Date(this.displayProposalsDayBlocks![0]?.dateBlock?.date || '').getTime(),
      );
    }
  }

  forceCurrentOptionWhenNotProvided(proposal: any, order: TrackingInfo) {
    const currentProposalDate = format(new Date(order.trackingInfo.dropoffWindowStart || ''), 'yyyy-MM-dd');
    if (proposal.date !== currentProposalDate) return;
    const currentProposalDayPart = this.order.proposalLabel;
    if (
      proposal.summarizedProposals.find((prop: { dayPart: string }) => prop.dayPart === currentProposalDayPart)
        ?.available
    )
      return;
    proposal.isEmpty = false;
    proposal.summarizedProposals.forEach((p: any) => {
      if (p.dayPart === currentProposalDayPart) {
        p.current = true;
      }
    });
  }

  getShorterPropArray(array: Array<UIproposalDayBlock>) {
    while (array[0] && array[0].empty) {
      array.shift();
    }
    const originalDateIndex = array.findIndex((el) => el?.dateBlock?.isDayOfTheOriginalOrder);
    const newArray = [array[originalDateIndex]];
    for (let i = 1; i < 7; i++) {
      if (
        array[originalDateIndex - i] &&
        !array[originalDateIndex - 1]?.empty &&
        newArray.filter((p) => !p.empty)?.length < 3
      ) {
        newArray.unshift(array[originalDateIndex - i]);
      }
      if (
        array[originalDateIndex + i] &&
        !array[originalDateIndex + 1]?.empty &&
        newArray.filter((p) => !p.empty)?.length < 3
      ) {
        newArray.push(array[originalDateIndex + i]);
      }
    }
    return newArray;
  }

  setProposalsDateBlocks(proposals?: Array<UIproposal>) {
    if (!proposals) return;
    this.proposalsDayBlocks = [];
    proposals.forEach((p, i) => {
      if (p.isEmpty && this.proposalsDayBlocks) {
        if (this.proposalsDayBlocks[this.proposalsDayBlocks.length - 1]?.empty) {
          let datesArray = this.proposalsDayBlocks[this.proposalsDayBlocks.length - 1].emptyDates;
          if (datesArray && datesArray.length > 1) {
            datesArray = datesArray.slice(0, 1);
          }
          datesArray?.push(p.formattedTime || '');
          this.proposalsDayBlocks[this.proposalsDayBlocks.length - 1].requestedDate =
            this.proposalsDayBlocks[this.proposalsDayBlocks.length - 1].requestedDate || p.requestedDate;
        } else {
          this.proposalsDayBlocks.push({
            empty: true,
            emptyDates: [p.formattedTime!],
            requestedDate: p.requestedDate,
          });
        }
        this.formatLastEmptyBlockTitle();
      } else {
        this.proposalsDayBlocks!.push({
          empty: false,
          dateBlock: p,
        });
      }
    });
    return proposals;
  }

  formatLastEmptyBlockTitle() {
    this.proposalsDayBlocks[this.proposalsDayBlocks!.length - 1].formattedTime =
      this.proposalsDayBlocks[this.proposalsDayBlocks.length - 1]?.emptyDates?.join(' - ');
  }

  onCancel() {
    this.emitCancel.emit();
  }

  onSeeMoreOptions() {
    this.displayProposalsDayBlocks = this.proposalsDayBlocks;
  }

  onSeeEarlierOptions() {
    const preArray = [];
    for (let i = 0; i < this.proposalsDayBlocks.length; i++) {
      const pFormattedTime =
        this.proposalsDayBlocks[i].dateBlock?.formattedTime || this.proposalsDayBlocks[i].formattedTime;
      const firstDisplayFormattedTime =
        this.displayProposalsDayBlocks![0].dateBlock?.formattedTime || this.displayProposalsDayBlocks![0].formattedTime;
      if (pFormattedTime !== firstDisplayFormattedTime) {
        preArray.push(this.proposalsDayBlocks[i]);
      } else {
        break;
      }
    }
    this.displayProposalsDayBlocks = [...preArray, ...(this.displayProposalsDayBlocks || [])];
    this.showEarlierOptionsButton = false;
    setTimeout(() => {
      const currentElement = document.querySelector('.was-first');
      currentElement?.scrollIntoView({ behavior: 'instant', block: 'center' });
      document.querySelector('common-edit-order-change-date-time')?.scrollBy(0, 160);
    }, 0);
  }

  onSeeLaterOptions() {
    const postArray = [];
    for (let i = this.proposalsDayBlocks.length - 1; i >= 0; i--) {
      const pFormattedTime =
        this.proposalsDayBlocks[i].dateBlock?.formattedTime || this.proposalsDayBlocks[i].formattedTime;
      const lastDisplayFormattedTime =
        this.displayProposalsDayBlocks![this.displayProposalsDayBlocks!.length - 1].dateBlock?.formattedTime ||
        this.displayProposalsDayBlocks![0].formattedTime;
      if (pFormattedTime !== lastDisplayFormattedTime) {
        postArray.unshift(this.proposalsDayBlocks[i]);
      } else {
        break;
      }
    }
    this.displayProposalsDayBlocks = [...(this.displayProposalsDayBlocks || []), ...postArray];
    this.showLaterOptionsButton = false;
  }

  onCloseCalendar() {
    this.isCalendarOpen = false;
  }

  onOpenCalendar() {
    this.isCalendarOpen = true;
  }

  isRequestedDate(timeProposal: string, requestedDate?: string) {
    if (!requestedDate || !timeProposal) return false;
    return isSameDay(new Date(timeProposal), new Date(requestedDate));
  }

  isCurrentProposal(timeProposal: TimeslotProposal, date: string): boolean {
    const orderStart = this.order?.trackingInfo?.dropoffWindowStart;
    const orderEnd = this.order?.trackingInfo?.dropoffWindowEnd;
    if (!orderStart || !orderEnd) return false;
    return (
      timeProposal.proposal?.estimatedDropoff?.start === orderStart &&
      timeProposal.proposal?.estimatedDropoff?.end === orderEnd
    );
  }

  selectNewProposal(prop: SummarizedProposal) {
    this.wasCurrentSelected = prop.current;
    this.selectedTime = prop.proposalId;
  }

  onSelectDate(date: any) {
    this.isCalendarOpen = false;
    this.proposalsDayBlocksShorter = undefined;
    this.proposalsDayBlocks = [];
    this.displayProposalsDayBlocks = undefined;
    this.gtm.clickEvent('Edit delivery/choose date/' + date);
    this.sendPostQuote(date);
  }

  submitChange() {
    this.loading = true;
    this.editOrderService.submitEditOrder({
      proposalId: this.selectedTime,
    });
  }

  protected readonly CalendarHeaderComponent = CalendarHeaderComponent;
}
